/**
 * Physiofit "Datenschutzinformation Nutzer" in a bottom drawer (ERS-42).
 *
 * The content is a static, in-repo HTML document imported at build time — never fetched, never
 * user-supplied. That is the entire basis for `dangerouslySetInnerHTML` here: the string is a
 * compile-time constant, so there is no injection surface. If this document ever becomes remote or
 * user-editable, this component must gain a sanitizer before that change lands.
 *
 * Regenerate the HTML from the Markdown source; never hand-edit it.
 * See `content/privacy/README.md`.
 */

import styled from 'styled-components';
import privacyDocumentHtml from '../../content/privacy/datenschutzinformation-nutzer.html?raw';
import { colors } from '../../../../styles/colors';
import { fontFamily, space, text } from '../../../../styles/onboardingUiTokens';
import { BottomDrawer } from '../elements/BottomDrawer';

export type PhysiofitPrivacyDrawerProps = {
  open: boolean;
  onClose: () => void;
};

/** The document's own <h1> is the accessible name of the dialog. */
const DOCUMENT_TITLE_ID = 'privacy-document-title';

const DocumentBody = styled.div`
  margin-bottom: ${space.lg};
  color: ${colors.medium};
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};

  h1,
  h2,
  h3,
  h4 {
    color: ${colors.dark};
    margin: ${space.lg} 0 ${space.xs};
    /* Headings render well above the inherited body line-height (${text.small.lineHeight}), which
       doesn't scale with their larger font-size and reads as cramped/overlapping. */
    line-height: 1.3;
  }

  h1 {
    margin-top: 0;
    font-family: ${fontFamily.lora};
  }

  p {
    margin: 0 0 ${space.sm};
  }

  ul {
    margin: 0 0 ${space.sm};
    padding-left: ${space.lg};
  }

  li {
    margin-bottom: ${space.xxs};
  }

  a {
    color: ${colors.primary};
    overflow-wrap: anywhere;
  }
`;

export function PhysiofitPrivacyDrawer({ open, onClose }: PhysiofitPrivacyDrawerProps) {
  return (
    <BottomDrawer open={open} onClose={onClose} aria-labelledby={DOCUMENT_TITLE_ID} gap={space.md}>
      <DocumentBody dangerouslySetInnerHTML={{ __html: privacyDocumentHtml }} />
    </BottomDrawer>
  );
}
